import { effect, inject, Injectable } from '@angular/core';

import { API_SUPPRESS_PROGRESS_HEADER, ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { AppLocalStorageService } from 'src/app/features/shared/storage/services/local-storage-services/app-local-storage.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { IThemeSettingsStorage } from 'src/app/features/shared/storage/interfaces/app-storage-tree.interface';
import { INavHistoryEntry } from '../models/nav-history-entry';

// De subset van de user-boom die naar de backend gaat: voorkeuren + history. De zware,
// herafleidbare caches (theme.cache, i18n.cache) blijven lokaal.
interface ISyncedState {
    theme?: { settings?: IThemeSettingsStorage };
    i18n?: { lang?: string };
    history?: INavHistoryEntry[];
}

const PUSH_DEBOUNCE_MS = 800;

// Achtergrond-sync zonder zichtbaar effect voor de gebruiker: telt niet mee voor de footer-progressbar
// (anders gaat die na elke tab-/paginawissel aan zodra de PUT even duurt).
const BACKGROUND_HEADERS = { [API_SUPPRESS_PROGRESS_HEADER]: 'true' };

// Centrale backend-synchronisatie van de geconsolideerde user-boom:
// - bij login (token van afwezig → aanwezig) is de server leidend en overschrijft hij
//   de gesyncte branches in de lokale boom;
// - tijdens de sessie wordt elke wijziging (debounced, fire-and-forget) gepusht.
@Injectable({ providedIn: 'root' })
export class UserStateSyncService {
    private readonly __api = inject(ApiService);
    private readonly __auth = inject(AuthService);
    private readonly __store = inject(AppLocalStorageService);

    private __wasAuthed = false;
    private __hydrating = false;
    private __pushTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        // Hydrate alleen op de overgang naar ingelogd (niet op elke token-refresh).
        effect(() => {
            const __authed = !!this.__auth.token$$();
            if (__authed && !this.__wasAuthed) {
                this.__wasAuthed = true;
                void this.hydrateFromBackend();
            } else if (!__authed) {
                this.__wasAuthed = false;
            }
        });

        // Debounced push wanneer de gesyncte subset verandert — maar niet tijdens een
        // hydratie (anders pushen we de net-ontvangen serverwaarde meteen terug) en
        // alleen als er een token is.
        effect(() => {
            const __subset = this.__syncedSubset();   // tracks theme.settings, i18n.lang, history
            if (this.__hydrating || !this.__auth.Token) return;
            this.__schedulePush(__subset);
        });
    }

    async hydrateFromBackend(): Promise<void> {
        if (!this.__auth.Token) return;
        try {
            const { data } = await this.__api.GET('/api/UserState', { headers: BACKGROUND_HEADERS });
            if (!data?.json) return;
            const __state = JSON.parse(data.json) as Partial<ISyncedState>;

            this.__hydrating = true;
            try {
                if (__state.theme?.settings) this.__store.appData$$.theme.settings.set(__state.theme.settings);
                if (__state.i18n?.lang) this.__store.appData$$.i18n.lang.set(__state.i18n.lang);
                if (__state.history) this.__store.appData$$.history.set(__state.history);
            } finally {
                this.__hydrating = false;
            }
        } catch {
            // Offline / niet geauthenticeerd — de lokale boom blijft leidend.
        }
    }

    private __syncedSubset(): ISyncedState {
        return {
            theme: { settings: this.__store.appData$$.theme.settings() },
            i18n: { lang: this.__store.appData$$.i18n.lang() },
            history: this.__store.appData$$.history() ?? [],
        };
    }

    private __schedulePush(subset: ISyncedState): void {
        if (this.__pushTimer) clearTimeout(this.__pushTimer);
        this.__pushTimer = setTimeout(() => void this.__push(subset), PUSH_DEBOUNCE_MS);
    }

    // Ten hoogste ÉÉN PUT tegelijk onderweg: de debounce-timer voorkomt alleen te frequent
    // hérplannen, niet dat een NIEUWE push start terwijl een vorige nog in-flight is (bv. omdat
    // de server traag reageert). Zonder deze guard stapelen overlappende PUT's op elkaar — elke
    // extra request wil dezelfde User-rij X-locken die de vorige (nog actieve) request al vasthoudt,
    // wat de backend in een oplopende wachtrij op die rij duwt (zag eruit als een complete hang).
    // De laatst binnengekomen subset tijdens een lopende push wordt bewaard en na afloop ALSNOG
    // gepusht (niet zomaar overgeslagen), zodat geen wijziging verloren gaat.
    private __pushing = false;
    private __pendingSubset: ISyncedState | null = null;

    private async __push(subset: ISyncedState): Promise<void> {
        if (this.__pushing) {
            this.__pendingSubset = subset;
            return;
        }

        this.__pushing = true;
        try {
            await this.__api.PUT('/api/UserState', { body: { json: JSON.stringify(subset) }, headers: BACKGROUND_HEADERS });
        } catch {
            // Offline — lokale boom is leidend; de volgende wijziging probeert opnieuw.
        } finally {
            this.__pushing = false;
            if (this.__pendingSubset) {
                const __next = this.__pendingSubset;
                this.__pendingSubset = null;
                void this.__push(__next);
            }
        }
    }
}
